import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  getPlayerData,
  getRecoveryError,
  getRecoveryStatus,
} from "core-integration/src/store/player/reducers";
import { accountRecovery } from "core-integration/src/store/player/thunks";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "rebass/styled-components";
import styled, { css } from "styled-components";
import Alert from "./Alert";
import Button from "./Button";
import Copy from "./Copy";
import { FieldWrap, InputField } from "./FieldRenderers";
import SubHeading from "./SubHeading";

const StyledSubHeading = styled(SubHeading)`
  margin: ${({ theme }) => theme.space[6]} 0;
`;

const FieldStyles = css`
  flex: 1;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    padding-inline-end: ${({ theme }) => theme.space[2]};
  }
`;

const Username = styled(FieldWrap)`
  ${FieldStyles}
`;

const Password = styled(FieldWrap)`
  ${FieldStyles}
`;

const SubmitButtonWrap = styled.div`
  flex: 1;
  gap: ${({ theme }) => theme.space[2]};
  margin-bottom: ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    align-items: center;
    margin-bottom: 1.8rem;
  }
`;

const SubmitButton = styled(Button)`
  flex: 1;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    flex-direction: row;
    align-items: end;
    justify-content: center;
  }
`;

const Title = styled.h2`
  margin-top: 0;
  margin-bottom: ${({ theme }) => theme.space[4]};
  font-family: ${({ theme }) => theme.fonts.impact};
`;

const AccountRecovery: React.FC = () => {
  const player = useSelector((state: RootState) => getPlayerData(state));
  const error = useSelector((state: RootState) => getRecoveryError(state));
  const recoveryStatus = useSelector((state: RootState) =>
    getRecoveryStatus(state),
  );

  const reduxDispatch = useDispatch<ThunkDispatch>();

  const doRecovery = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    reduxDispatch(
      accountRecovery({
        email,
        password,
      }),
    );
  };
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (recoveryStatus) {
      setSuccess(true);
    }
  }, [recoveryStatus]);

  if (player && player.id) {
    return (
      <Box mb={4} p={4}>
        <Copy>
          <p>
            {t("login.loggedInAs", "Welcome back {{firstName}} {{lastName}}", {
              firstName: player.first_name,
              lastName: player.last_name,
            })}
          </p>
        </Copy>
      </Box>
    );
  }

  return (
    <Box mb={4} p={4}>
      {error && !success && (
        <Box mb={2}>
          <Alert type="error">
            {t(
              "recovery.error",
              "Something went wrong. Please contact support.",
            )}
          </Alert>
        </Box>
      )}
      <Title>{t("recovery.title", "Account recovery")}</Title>
      {success ? (
        <StyledSubHeading>
          {t(
            "recovery.success",
            "Thank you, we're validating your details. Our support team will contact you in due course.",
          )}
        </StyledSubHeading>
      ) : (
        <>
          <StyledSubHeading>
            {t(
              "recovery.subHeading",
              "Enter the last known email address and password you remember for this account.",
            )}
          </StyledSubHeading>
          <Form onSubmit={doRecovery}>
            <Username>
              <InputField
                id="loginEmail"
                name="email"
                type="email"
                label={t("recovery.form.emailLabel", "Email")}
                onChange={(e) => setEmail(e.target.value)}
                value={email}
                hint={t(
                  "recovery.form.emailLabelHint",
                  "The email associated with your account",
                )}
              />
            </Username>
            <Password>
              <InputField
                id="loginPassword"
                name="password"
                type="password"
                label={t("recovery.form.pwd", "Password")}
                onChange={(e) => setPassword(e.target.value)}
                value={password}
                hint={t(
                  "recovery.form.pwdLabelHint",
                  "Your last known password",
                )}
              />
            </Password>
            <SubmitButtonWrap>
              <SubmitButton
                width={1}
                type="submit"
                disabled={!(email && password)}
              >
                {t("recovery.form.submitCta", "Submit")}
              </SubmitButton>
            </SubmitButtonWrap>
          </Form>
        </>
      )}
    </Box>
  );
};

export default AccountRecovery;
